import { HMOService, clinicTypes } from '../consts'
import {
  isDmServiceAvailable,
  isDmServiceSelected,
  isHtnServiceAvailable,
  isHtnServiceSelected,
  isMhServiceAvailable,
  isMhServiceSelected,
  isPrimaryServiceAvailable,
  isPrimaryServiceSelected
} from './clinicsUtils'
import { filterCanEditPersonalData } from './familyPermissionsUtils'
import { isMinor } from './familyUtils'
import { getIsHmoMember } from './hmoUtils'
import { serializeLocationVerification } from './serializerUtil'

export const filterAppointmentsInQueue = appointments =>
  appointments?.filter(
    appoint =>
      appoint.status === 'paid' ||
      appoint.status === 'confirmed' ||
      appoint.status === 'granted' ||
      appoint.status === 'frozen'
  )
export const filterAppointmentsLater = appointments =>
  appointments.filter(appoint => appoint.status === 'scheduled')

export const filterPendingAppointments = appointments =>
  appointments.filter(appoint => appoint.status === 'pending')

export const filterReferrerAndPendingAppointments = appointments =>
  appointments.filter(
    appoint =>
      appoint.status === 'pending' &&
      (!appoint.metadata || !('referrer_org' in appoint.metadata))
  )

export const validAppointments = appointments => {
  return appointments
    ?.filter(
      appointment =>
        appointment.status === 'confirmed' ||
        appointment.status === 'paid' ||
        appointment.status === 'granted' ||
        appointment.status === 'scheduled' ||
        appointment.status === 'pending' ||
        appointment.status === 'frozen'
    )
    .sort((a, b) => (a.status !== 'scheduled' ? -1 : 1))
}

export const earliestAppointments = appointments => {
  return appointments?.filter(
    appointment =>
      appointment.status === 'confirmed' || appointment.status === 'paid'
  )
}

export const filterHTNAppointments = appointments =>
  appointments?.filter(
    appointment => appointment.antidote_clinic === clinicTypes.HTN
  )

export const filterMHAppointments = appointments =>
  appointments?.filter(
    appointment => appointment.antidote_clinic === clinicTypes.MENTAL
  )

export const filterDiabetesAppointments = appointments =>
  appointments?.filter(
    appointment => appointment.antidote_clinic === clinicTypes.DIABETES
  )

export const filterMHPendingAppointments = appointments =>
  appointments?.filter(
    appointment =>
      appointment.antidote_clinic === clinicTypes.MENTAL &&
      appointment.status === 'pending'
  )

export const getOtherParticipants = (data, userId) => {
  const participants = data?.appointment?.participants
  const otherParticipants = participants
    .filter(participant => participant.id !== 'p_' + userId)
    .map(participant => participant.name)
    .sort((a, b) => (b.includes('Dr.') ? 1 : -1))
    .join(', ')
  const otherParticipantObj = {
    name: otherParticipants,
    speciality: participants
      .filter(participant => participant.id !== 'p_' + userId)
      .map(participant => participant.speciality)
  }
  return otherParticipantObj
}

export const isNeedToAskMyPharmacyUpdate = state => {
  const userDetails = state?.profile?.userDetails
  const selectedMember = state?.account?.selectedMember
  const isChild = isMinor(state)

  return (
    !isChild &&
    !(selectedMember?.id
      ? selectedMember?.pharmacy?.PharmacyId ||
        !filterCanEditPersonalData(selectedMember)
      : userDetails?.pharmacy?.PharmacyId)
  )
}

export const isNeedToAskDesignatedDoctor = state => {
  return (
    !!getIsHmoMember(state.profile?.userDetails) && !state.getCare?.isPCPFlow
  )
}

export const validateChangeAppointment = lastCancelTime => {
  return new Date() < new Date(lastCancelTime)
}

export const filterPrimaryPendingAppointments = appointments =>
  appointments?.filter(
    appointment =>
      // primary/primaryHMO
      appointment.antidote_clinic?.includes(clinicTypes.PRIMARY) &&
      appointment.status === 'pending'
  )

export const filterHTNFollowUpAppointments = appointments =>
  appointments?.filter(
    appointment =>
      appointment.antidote_clinic === clinicTypes.HTN &&
      appointment.is_clinic_follow_up
  )
export const filterDiabetesFollowUpAppointments = appointments =>
  appointments?.filter(
    appointment =>
      appointment.antidote_clinic === clinicTypes.DIABETES &&
      appointment.is_clinic_follow_up
  )

export const filterMHFollowUpAppointments = appointments =>
  appointments?.filter(
    appointment =>
      appointment.antidote_clinic === clinicTypes.MENTAL &&
      appointment.is_clinic_follow_up
  )

export const filterDMFollowUpAppointments = appointments =>
  appointments?.filter(
    appointment =>
      appointment.antidote_clinic === clinicTypes.DIABETES &&
      appointment.is_clinic_follow_up
  )

export const filterPrimaryHmoFollowUpAppointments = appointments =>
  appointments?.filter(
    appointment =>
      appointment.antidote_clinic === clinicTypes.PRIMARY + HMOService &&
      appointment.is_clinic_follow_up
  )

export const filterHTNFollowUpAppointmentsInNextWeek = appointments =>
  appointments?.filter(
    appointment =>
      appointment.antidote_clinic === clinicTypes.HTN &&
      appointment.is_clinic_follow_up &&
      new Date(appointment.schduled_time).getDate() - 7 <= new Date().getDate()
  )

export const filterDiabetesFollowUpAppointmentsInNextWeek = appointments =>
  appointments?.filter(
    appointment =>
      appointment.antidote_clinic === clinicTypes.DIABETES &&
      appointment.is_clinic_follow_up &&
      new Date(appointment.schduled_time).getDate() - 7 <= new Date().getDate()
  )

export const filterPrimaryQueueAppointments = appointments =>
  appointments.filter(appointment =>
    appointment?.antidote_clinic?.includes(clinicTypes.PRIMARY)
  )

export const filterQueueAppointmentsClinic = (appointments, clinic) =>
  appointments.filter(appointment => appointment?.antidote_clinic === clinic)

export const filterPendingInitialAppointment = appointments =>
  appointments?.find(
    appointment =>
      appointment.status === 'pending' &&
      (!appointment?.is_clinic_follow_up || appointment?.is_created_by_patient)
  )

export const getPendingAppointment = appointments =>
  appointments?.find(appointment => appointment.status === 'pending')

export const getQueueAppointment = appointments =>
  appointments?.find(
    appoint =>
      appoint?.status === 'paid' ||
      appoint?.status === 'confirmed' ||
      appoint?.status === 'granted' ||
      appoint?.status === 'frozen'
  )

export const isPendingAppointment = appointment =>
  appointment?.status === 'pending'
export const isQueueAppointment = appoint =>
  appoint?.status === 'paid' ||
  appoint?.status === 'confirmed' ||
  appoint?.status === 'granted' ||
  appoint?.status === 'frozen'
export const isLaterAppointment = appointment =>
  appointment?.status === 'scheduled'

export const needsToSkipHTNSchedule = state =>
  state.getCare?.isHTNFollowUp &&
  isHtnServiceAvailable(state) &&
  isHtnServiceSelected(state) &&
  filterHTNFollowUpAppointments(state.appointments?.appointments)?.length > 0

export const needsToSkipDMSchedule = state =>
  state.getCare?.isDMFollowUp &&
  isDmServiceAvailable(state) &&
  isDmServiceSelected(state) &&
  filterDMFollowUpAppointments(state.appointments?.appointments)?.length > 0

export const needsToSkipMHSchedule = state =>
  state.getCare?.isMHFollowUp &&
  isMhServiceAvailable(state) &&
  isMhServiceSelected(state) &&
  filterMHFollowUpAppointments(state.appointments?.appointments)?.length > 0

export const needsToSkipPrimaryHmoSchedule = state =>
  state.getCare?.isPrimaryHMOFollowUp &&
  isPrimaryServiceAvailable(state) &&
  isPrimaryServiceSelected(state) &&
  filterPrimaryHmoFollowUpAppointments(state.appointments?.appointments)
    ?.length > 0

export const isNeedToSkipScheduleAppointment = state =>
  needsToSkipHTNSchedule(state) ||
  needsToSkipMHSchedule(state) ||
  needsToSkipDMSchedule(state) ||
  needsToSkipPrimaryHmoSchedule(state)

export const filterAppointmentsPerMember = (appointments, memberId) => {
  return appointments?.filter(
    appointment => appointment?.participants?.[0]?.id === 'p_' + memberId
  )
}

export const filterPrimaryAppointmentsPerMember = (appointments, memberId) => {
  return appointments?.filter(
    appointment =>
      appointment?.participants?.[0]?.id === 'p_' + memberId &&
      appointment?.antidote_clinic?.includes(clinicTypes.PRIMARY)
  )
}

export const getAppointmentMemberId = appointment => {
  const memberId = appointment?.participants?.[0]?.id?.split('p_')?.[1]
  return memberId
}

export const getTimeWindowsContext = state => {
  const isHMOMember = getIsHmoMember(state.profile?.userDetails)
  const selectedService =
    state.getCare?.selectedService === clinicTypes.PRIMARY && isHMOMember
      ? clinicTypes.PRIMARY + HMOService
      : state.getCare?.selectedService
  const designatedPk = state.account?.personalDoctor?.personalDoctor?.id
  const locationState = serializeLocationVerification(state).address_state
  const appointmentLocation = state.appointments?.appointmentLocation
  const isDesignatedFlow =
    state.getCare?.isPCPFlow?.designated === true ||
    state.appointments?.designatedPk != null

  return {
    selectedService,
    designatedPk,
    locationState,
    appointmentLocation,
    isDesignatedFlow
  }
}

export const buildTimeWindowsUrl = (state, options = {}) => {
  const { designated } = options
  const context = getTimeWindowsContext(state)
  const useDesignatedFlow =
    designated === undefined ? context.isDesignatedFlow : designated

  let url = useDesignatedFlow
    ? `/schedule/get-time-windows/?doctorId=${context.designatedPk}`
    : `/schedule/get-time-windows/?clinic=${context.selectedService}`

  if (context.selectedService) {
    url = url + `&clinic=${context.selectedService}`
  }
  if (context.locationState != null) {
    url = url + `&state=${context.locationState}`
  } else if (!!context.appointmentLocation) {
    url = url + `&state=${context.appointmentLocation}`
  }

  return { url, context }
}

export const isSelectedTimeWindowStillAvailable = (
  timeWindows,
  selectedOption
) =>
  timeWindows?.some(
    slot => slot?.option === selectedOption && slot?.status !== 'disabled'
  )
